import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type DataTypeType } from '@repo/advanced-filters';
import { LocalStorage } from '@vectord/utils';
import { type ColumnType } from 'antd/es/table';

const lsInstance = LocalStorage.getInstance('store.table.width');

export enum UniqueTableType {
  Leads = 'leads',
  Tasks = 'tasks',
  Activities = 'activities',
  AutomationList = 'automationList',
  ManageLists = 'manageLists',
  ListDetails = 'listDetails',
  SettingsRequests = 'settingsRequests',
  SettingsPermissionTemplates = 'settingsPermissionTemplates',
  SettingsManageUsers = 'settingsManageUsers',
  SettingsSmartViewDashboard = 'settingsSmartViewDashboard',
  SettingsSalesGroups = 'settingsSalesGroups',
  SettingsLeadFields = 'settingsLeadFields',
  SettingsCoreActivities = 'settingsCoreActivities',
  SettingsCustomActivities = 'settingsCustomActivities',
  SettingsLeadForms = 'settingsLeadForms',
  SettingsEmailTemplates = 'settingsEmailTemplates',
  SettingsTelephonyNumbers = 'settingsTelephonyNumbers',
  ScheduledAiCalls = 'scheduledAiCalls',
}

export interface ModifiedColumn extends ColumnType<any> {
  dataIndex: string;
  dataType?: DataTypeType;
  canReorder?: boolean;
  canResize?: boolean;
  canWordWrap?: boolean;
  canHide?: boolean;
}

export const DEFAULT_NEW_COLUMN_WIDTH = 200;

export type InitialStateType = Partial<Record<UniqueTableType, {
  width: Record<string, number>;
  wordWrap: Record<string, boolean>;
}>>;

export const initialState: InitialStateType = {};

const defaultState: InitialStateType['activities'] = {
  width: {},
  wordWrap: {},
};

const savedConfig: InitialStateType = lsInstance?.config as InitialStateType;

export const tableSlice = createSlice({
  name: 'table',
  initialState: { ...initialState, ...savedConfig },
  reducers: {
    setWidth: (state, action: PayloadAction<{
      tableKey: UniqueTableType;
      columnDataIndex: string;
      width: number;
    }>) => {
      const { tableKey, columnDataIndex, width } = action.payload;
      if (!state[tableKey]) {
        state[tableKey] = defaultState;
      }

      state[tableKey] = {
        ...state[tableKey],
        width: {
          ...state[tableKey]!.width,
          [columnDataIndex]: width,
        },
      };
    },
    deleteWidth: (state, action: PayloadAction<{
      tableKey: UniqueTableType;
      columnDataIndex: string;
    }>) => {
      const { tableKey, columnDataIndex } = action.payload;
      if (!state[tableKey]) return;

      delete state[tableKey]!.width[columnDataIndex];
    },
    // Create widths only if they don't exist in the state
    createWidthsIfNotExists: (state, action: PayloadAction<{
      tableKey: UniqueTableType;
      columnDataIndex: string[];
    }>) => {
      const { tableKey, columnDataIndex } = action.payload;
      if (!state[tableKey]) state[tableKey] = defaultState;

      columnDataIndex.forEach((column) => {
        // if a column already have custom config, don't override it
        if (state[tableKey]!.width[column]) return;

        state[tableKey]!.width[column] = DEFAULT_NEW_COLUMN_WIDTH;
        state[tableKey]!.wordWrap[column] = true;
      });
    },
    setWordWrap: (state, action: PayloadAction<{
      tableKey: UniqueTableType;
      columnDataIndex: string;
      wordWrap: boolean;
    }>) => {
      const { tableKey, columnDataIndex, wordWrap } = action.payload;
      if (!state[tableKey]) state[tableKey] = defaultState;

      state[tableKey] = {
        ...state[tableKey],
        wordWrap: {
          ...state[tableKey]!.wordWrap,
          [columnDataIndex]: wordWrap,
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('table/'),
      (state) => { lsInstance.config = state; },
    );
  },
});

export const {
  setWidth,
  deleteWidth,
  createWidthsIfNotExists,
  setWordWrap,
} = tableSlice.actions;

export default tableSlice.reducer;
